import { apiBaseUrl, auth } from "@/firebase/app";

// Mirrors Services/SBotFunctionHTTPService.swift: Bearer ID token + JSON body,
// error payloads decoded from `{ "error": "..." }`.

export class FunctionsRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "FunctionsRequestError";
    this.status = status;
  }
}

export async function currentIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new FunctionsRequestError("Sign in again before continuing.", 401);
  }
  return user.getIdToken();
}

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
}

export async function apiRequest<Response = unknown>(path: string, options: ApiRequestOptions = {}): Promise<Response> {
  const token = await currentIdToken();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const method = options.method ?? (options.body !== undefined ? "POST" : "GET");

  const response = await fetch(`${apiBaseUrl}${normalizedPath}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    // JSON.stringify serializes Dates as ISO-8601, matching the iOS client.
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  const text = await response.text();
  if (!response.ok) {
    let message = `Request failed with status ${response.status}.`;
    try {
      const parsed = JSON.parse(text) as { error?: unknown };
      if (parsed && typeof parsed.error === "string" && parsed.error.length > 0) {
        message = parsed.error;
      }
    } catch {
      // Non-JSON error body; keep the generic message.
    }
    throw new FunctionsRequestError(message, response.status);
  }

  if (!text) {
    return undefined as Response;
  }
  return JSON.parse(text) as Response;
}
